import { Command } from 'commander';
import { TeamManager, TeamMember } from '../core/team-manager';
import { WorkflowManager } from '../core/workflow-manager';
import { TeamSyncManager } from '../core/team-sync';
import { logger } from '../utils/logger';
import { withErrorHandling } from '../utils/error-handler';
import pc from 'picocolors';
import { Table } from 'console-table-printer';
import inquirer from 'inquirer';

export function createTeamCommand(): Command {
  const command = new Command('team');
  
  command
    .description('Team collaboration features')
    .alias('tm');

  // Initialize team
  command
    .command('init')
    .description('Initialize team for the project')
    .option('-n, --name <name>', 'Team name')
    .action(withErrorHandling(async (options) => {
      const teamManager = new TeamManager();
      await teamManager.initialize();
      
      const team = await teamManager.createTeam(options.name);
      
      console.log(pc.green('✅ Team initialized successfully!'));
      console.log(`Team ID: ${pc.cyan(team.id)}`);
      console.log(`Team Name: ${pc.cyan(team.name)}`);
      
      // Prompt to add first member
      const { addMember } = await inquirer.prompt([{
        type: 'confirm',
        name: 'addMember',
        message: 'Would you like to add yourself as a team member?',
        default: true
      }]);
      
      if (addMember) {
        const memberInfo = await inquirer.prompt([
          {
            type: 'input',
            name: 'name',
            message: 'Your name:',
            validate: (input) => input.length > 0
          },
          {
            type: 'input',
            name: 'email',
            message: 'Your email:',
            validate: (input) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input)
          },
          {
            type: 'list',
            name: 'role',
            message: 'Your role:',
            choices: ['owner', 'admin', 'member'],
            default: 'owner'
          },
          {
            type: 'input',
            name: 'skills',
            message: 'Your skills (comma-separated):',
            filter: (input) => input.split(',').map(s => s.trim()).filter(s => s)
          }
        ]);
        
        const member = await teamManager.addMember({
          name: memberInfo.name,
          email: memberInfo.email,
          role: memberInfo.role,
          skills: memberInfo.skills
        });
        
        await teamManager.setCurrentUser(member.id);
        
        console.log(pc.green(`\n✅ Added ${member.name} to the team!`));
      }
    }));

  // Add team member
  command
    .command('add')
    .description('Add a team member')
    .action(withErrorHandling(async () => {
      const teamManager = new TeamManager();
      await teamManager.initialize();
      
      const team = teamManager.getTeam();
      if (!team) {
        console.log(pc.red('No team found. Run "workflow team init" first.'));
        return;
      }
      
      const memberInfo = await inquirer.prompt([
        {
          type: 'input',
          name: 'name',
          message: 'Member name:',
          validate: (input) => input.length > 0
        },
        {
          type: 'input',
          name: 'email',
          message: 'Member email:',
          validate: (input) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input)
        },
        {
          type: 'list',
          name: 'role',
          message: 'Member role:',
          choices: ['admin', 'member', 'viewer'],
          default: 'member'
        },
        {
          type: 'input',
          name: 'skills',
          message: 'Skills (comma-separated):',
          filter: (input) => input.split(',').map(s => s.trim()).filter(s => s)
        },
        {
          type: 'input',
          name: 'timezone',
          message: 'Timezone:',
          default: 'UTC'
        }
      ]);
      
      const member = await teamManager.addMember({
        name: memberInfo.name,
        email: memberInfo.email,
        role: memberInfo.role,
        skills: memberInfo.skills,
        timezone: memberInfo.timezone
      });
      
      console.log(pc.green(`✅ Added ${member.name} to the team!`));
      console.log(`Member ID: ${pc.cyan(member.id)}`);
    }));

  // List team members
  command
    .command('list')
    .description('List all team members')
    .alias('ls')
    .action(withErrorHandling(async () => {
      const teamManager = new TeamManager();
      await teamManager.initialize();
      
      const team = teamManager.getTeam();
      if (!team) {
        console.log(pc.red('No team found. Run "workflow team init" first.'));
        return;
      }
      
      const currentUser = teamManager.getCurrentUser();
      
      console.log(pc.bold(`\n👥 ${team.name}`));
      if (team.description) {
        console.log(pc.dim(team.description));
      }
      console.log();
      
      const table = new Table({
        columns: [
          { name: 'name', title: 'Name', alignment: 'left' },
          { name: 'email', title: 'Email', alignment: 'left' },
          { name: 'role', title: 'Role', alignment: 'left' },
          { name: 'status', title: 'Status', alignment: 'left' },
          { name: 'skills', title: 'Skills', alignment: 'left' }
        ]
      });
      
      team.members.forEach(member => {
        const statusIcon = member.availability === 'available' ? '🟢' :
                          member.availability === 'busy' ? '🟡' :
                          member.availability === 'away' ? '🟠' : '⚫';
        
        const isCurrentUser = currentUser?.id === member.id;
        
        table.addRow({
          name: isCurrentUser ? pc.cyan(`${member.name} (You)`) : member.name,
          email: member.email,
          role: member.role,
          status: `${statusIcon} ${member.availability}`,
          skills: member.skills.join(', ')
        });
      });
      
      table.printTable();
    }));

  // Set current user
  command
    .command('me')
    .description('Set or show current user')
    .argument('[memberId]', 'Member ID to set as current user')
    .action(withErrorHandling(async (memberId) => {
      const teamManager = new TeamManager();
      await teamManager.initialize();
      
      if (memberId) {
        // Set current user
        await teamManager.setCurrentUser(memberId);
        const member = teamManager.getMemberById(memberId);
        if (member) {
          console.log(pc.green(`✅ Set current user to ${member.name}`));
        }
      } else {
        // Show current user
        const currentUser = teamManager.getCurrentUser();
        if (currentUser) {
          console.log(pc.bold('\n👤 Current User:'));
          console.log(`Name: ${pc.cyan(currentUser.name)}`);
          console.log(`Email: ${currentUser.email}`);
          console.log(`Role: ${currentUser.role}`);
          console.log(`Status: ${currentUser.availability}`);
          if (currentUser.skills.length > 0) {
            console.log(`Skills: ${currentUser.skills.join(', ')}`);
          }
        } else {
          console.log(pc.yellow('No current user set. Use "workflow team me <memberId>" to set.'));
        }
      }
    }));

  // Update member status
  command
    .command('status')
    .description('Update your availability status')
    .argument('<status>', 'Status (available/busy/away/offline)')
    .action(withErrorHandling(async (status) => {
      const teamManager = new TeamManager();
      await teamManager.initialize();
      
      const currentUser = teamManager.getCurrentUser();
      if (!currentUser) {
        console.log(pc.red('No current user set. Use "workflow team me <memberId>" first.'));
        return;
      }
      
      const validStatuses = ['available', 'busy', 'away', 'offline'];
      if (!validStatuses.includes(status)) {
        console.log(pc.red(`Invalid status. Choose from: ${validStatuses.join(', ')}`));
        return;
      }
      
      await teamManager.updateMemberStatus(currentUser.id, status as any);
      console.log(pc.green(`✅ Updated status to ${status}`));
    }));

  // Assign task
  command
    .command('assign')
    .description('Assign current task to a team member')
    .argument('[memberId]', 'Member ID to assign to')
    .option('--suggest', 'Suggest best assignee based on skills')
    .action(withErrorHandling(async (memberId, options) => {
      const teamManager = new TeamManager();
      const workflowManager = new WorkflowManager();
      await teamManager.initialize();
      
      const taskState = await workflowManager.getTaskState();
      if (!taskState?.current) {
        console.log(pc.red('No active task to assign.'));
        return;
      }
      
      let assigneeId = memberId;
      
      if (options.suggest || !assigneeId) {
        // Suggest assignee
        const taskType = taskState.current.type || 'general';
        const suggested = await teamManager.suggestAssignee(taskType);
        
        if (suggested) {
          console.log(pc.bold('\n🎯 Suggested Assignee:'));
          console.log(`${suggested.name} (${suggested.email})`);
          console.log(`Skills: ${suggested.skills.join(', ')}`);
          
          if (!assigneeId) {
            const { confirm } = await inquirer.prompt([{
              type: 'confirm',
              name: 'confirm',
              message: `Assign to ${suggested.name}?`,
              default: true
            }]);
            
            if (confirm) {
              assigneeId = suggested.id;
            }
          }
        } else {
          console.log(pc.yellow('No suitable assignee found.'));
          return;
        }
      }
      
      if (!assigneeId) {
        // Show member list to choose
        const members = teamManager.getAvailableMembers();
        if (members.length === 0) {
          console.log(pc.red('No available team members.'));
          return;
        }
        
        const { selectedId } = await inquirer.prompt([{
          type: 'list',
          name: 'selectedId',
          message: 'Select assignee:',
          choices: members.map(m => ({
            name: `${m.name} (${m.skills.join(', ')})`,
            value: m.id
          }))
        }]);
        
        assigneeId = selectedId;
      }
      
      // Perform assignment
      const currentUser = teamManager.getCurrentUser();
      const assignment = await teamManager.assignTask(
        taskState.current.id || 'current',
        assigneeId,
        currentUser?.id
      );
      
      // Update task state
      taskState.current.assignee = assigneeId;
      await workflowManager.updateTaskState(taskState);
      
      // Sync with team
      const syncManager = new TeamSyncManager(teamManager, workflowManager);
      await syncManager.initialize();
      await syncManager.broadcastTaskAssignment(
        taskState.current.id || 'current',
        assigneeId
      );
      
      const assignee = teamManager.getMemberById(assigneeId);
      console.log(pc.green(`✅ Task assigned to ${assignee?.name}`));
    }));

  // Show team activity
  command
    .command('activity')
    .description('Show recent team activity')
    .option('-n, --number <number>', 'Number of activities to show', '20')
    .action(withErrorHandling(async (options) => {
      const teamManager = new TeamManager();
      await teamManager.initialize();
      
      const activities = await teamManager.getRecentActivity(parseInt(options.number));
      
      if (activities.length === 0) {
        console.log(pc.yellow('No team activity found.'));
        return;
      }
      
      console.log(pc.bold('\n📊 Recent Team Activity:\n'));
      
      activities.forEach(activity => {
        const actor = teamManager.getMemberById(activity.actorId);
        const timestamp = new Date(activity.timestamp).toLocaleString();
        
        let message = '';
        switch (activity.type) {
          case 'task_assigned':
            const assignee = teamManager.getMemberById(activity.data?.assigneeId);
            message = `${actor?.name || 'Unknown'} assigned task to ${assignee?.name || 'Unknown'}`;
            break;
          case 'task_completed':
            message = `${actor?.name || 'Unknown'} completed a task`;
            break;
          case 'member_joined':
            message = `${actor?.name || 'Unknown'} joined the team`;
            break;
          case 'status_changed':
            message = `${actor?.name || 'Unknown'} changed status to ${activity.data?.availability || 'unknown'}`;
            break;
          default:
            message = `${actor?.name || 'Unknown'} performed ${activity.type}`;
        }
        
        console.log(`${pc.dim(timestamp)} - ${message}`);
      });
    }));

  // Team dashboard
  command
    .command('dashboard')
    .description('Show team dashboard with current tasks')
    .alias('dash')
    .action(withErrorHandling(async () => {
      const teamManager = new TeamManager();
      const workflowManager = new WorkflowManager();
      const syncManager = new TeamSyncManager(teamManager, workflowManager);
      
      await teamManager.initialize();
      await syncManager.initialize();
      
      const team = teamManager.getTeam();
      if (!team) {
        console.log(pc.red('No team found. Run "workflow team init" first.'));
        return;
      }
      
      console.log(pc.bold(`\n📊 ${team.name} Dashboard\n`));
      
      // Team status
      const availableMembers = teamManager.getAvailableMembers();
      console.log(pc.green(`✅ ${availableMembers.length} members available`));
      console.log(pc.yellow(`🔄 ${team.members.length - availableMembers.length} members busy/away`));
      console.log();
      
      // Get all team members' task states
      const teamStates = await syncManager.getTeamTaskStates();
      
      const table = new Table({
        columns: [
          { name: 'member', title: 'Member', alignment: 'left' },
          { name: 'task', title: 'Current Task', alignment: 'left' },
          { name: 'phase', title: 'Phase', alignment: 'left' },
          { name: 'duration', title: 'Duration', alignment: 'right' }
        ]
      });
      
      let hasActiveTasks = false;
      
      team.members.forEach(member => {
        const state = teamStates.get(member.id);
        const currentTask = state?.current;
        
        if (currentTask) {
          hasActiveTasks = true;
          const duration = currentTask.startTime ? 
            Math.floor((Date.now() - new Date(currentTask.startTime).getTime()) / 60000) : 0;
          
          table.addRow({
            member: member.name,
            task: currentTask.task || 'Untitled task',
            phase: currentTask.phase || 'unknown',
            duration: `${duration}m`
          });
        } else if (member.availability === 'available') {
          table.addRow({
            member: member.name,
            task: pc.dim('No active task'),
            phase: '-',
            duration: '-'
          });
        }
      });
      
      if (hasActiveTasks || availableMembers.length > 0) {
        table.printTable();
      } else {
        console.log(pc.dim('No team members with active tasks.'));
      }
      
      // Recent activity summary
      const activities = await teamManager.getRecentActivity(5);
      if (activities.length > 0) {
        console.log(pc.bold('\n🕐 Recent Activity:'));
        activities.forEach(activity => {
          const actor = teamManager.getMemberById(activity.actorId);
          const time = new Date(activity.timestamp).toLocaleTimeString();
          console.log(pc.dim(`${time} - ${actor?.name || 'Unknown'}: ${activity.type.replace('_', ' ')}`));
        });
      }
      
      await syncManager.stop();
    }));

  // Team settings
  command
    .command('settings')
    .description('Configure team settings')
    .action(withErrorHandling(async () => {
      const teamManager = new TeamManager();
      await teamManager.initialize();
      
      const currentSettings = teamManager.getSettings();
      
      const newSettings = await inquirer.prompt([
        {
          type: 'confirm',
          name: 'syncEnabled',
          message: 'Enable team sync?',
          default: currentSettings?.syncEnabled ?? true
        },
        {
          type: 'confirm',
          name: 'notificationsEnabled',
          message: 'Enable notifications?',
          default: currentSettings?.notificationsEnabled ?? true
        },
        {
          type: 'confirm',
          name: 'autoAssignEnabled',
          message: 'Enable auto-assignment?',
          default: currentSettings?.autoAssignEnabled ?? false
        },
        {
          type: 'list',
          name: 'taskAssignmentStrategy',
          message: 'Task assignment strategy:',
          choices: [
            { name: 'Manual', value: 'manual' },
            { name: 'Round Robin', value: 'round-robin' },
            { name: 'Load Balanced', value: 'load-balanced' },
            { name: 'Skill Based', value: 'skill-based' }
          ],
          default: currentSettings?.taskAssignmentStrategy || 'manual'
        }
      ]);
      
      await teamManager.updateSettings(newSettings);
      console.log(pc.green('✅ Team settings updated!'));
    }));

  return command;
}